import {
  confidenceBand,
  type ConfidenceBand,
  type DocumentExtractionDetail,
  type ExtractedField,
  type ExtractionIssue,
  labelForFieldPath,
} from '@hvac/contracts';
import { Alert, Button, Card, CardBody, CardHeader, cn, Spinner } from '@hvac/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  extractionKeys,
  latestExtractionQuery,
  requestExtraction,
} from '../../shared/api/extraction-queries.js';

/**
 * The extraction, beside the original.
 *
 * Read-only, deliberately and completely. Nothing here edits, accepts, or
 * posts, that is Phase 5's review engine. What this screen has to do is make
 * a reviewer able to decide, at a glance, whether the extraction can be
 * trusted. Three things carry that:
 *
 *  - **Findings first.** Validation warnings sit above the fields, because
 *    "the totals do not add up" changes how you read every number below it.
 *  - **Confidence on every field**, as a colour and a number. The number
 *    matters: a reviewer who sees 0.62 twice learns what the band means.
 *  - **Provenance, visibly.** Which prompt version, which model, which
 *    attempt. When an extraction looks wrong, that is the first question, and
 *    burying it in a database column means nobody ever asks it.
 */

const BAND_STYLES: Record<ConfidenceBand, { dot: string; text: string; label: string }> = {
  high: { dot: 'bg-success', text: 'text-content-muted', label: 'confident' },
  medium: { dot: 'bg-warning', text: 'text-content-muted', label: 'fairly sure' },
  low: { dot: 'bg-danger', text: 'text-danger', label: 'unsure' },
};

const FIELD_GROUPS: readonly { title: string; prefix: string }[] = [
  { title: 'Supplier', prefix: 'vendor.' },
  { title: 'Amounts', prefix: 'totals.' },
  { title: 'Dates', prefix: 'dates.' },
  { title: 'References', prefix: 'identifiers.' },
  { title: 'Payment', prefix: 'payment.' },
];

export interface ExtractionPanelProps {
  readonly organizationId: string;
  readonly documentId: string;
  readonly canRequest: boolean;
}

export function ExtractionPanel({ organizationId, documentId, canRequest }: ExtractionPanelProps) {
  const queryClient = useQueryClient();
  const query = useQuery(latestExtractionQuery(organizationId, documentId));

  const request = useMutation({
    mutationFn: (force: boolean) => requestExtraction(organizationId, documentId, force),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: extractionKeys.document(organizationId, documentId),
      });
    },
  });

  if (query.isPending) {
    return (
      <Card>
        <CardBody className="flex justify-center py-8">
          <Spinner className="size-5 text-brand-600" label="Loading the extraction" />
        </CardBody>
      </Card>
    );
  }

  const extraction = query.data ?? null;

  if (extraction === null) {
    return (
      <Card>
        <CardHeader title="Extraction" description="Nothing has read this document yet." />
        <CardBody>
          {canRequest ? (
            <Button onClick={() => request.mutate(false)} disabled={request.isPending}>
              {request.isPending ? 'Queueing…' : 'Extract this document'}
            </Button>
          ) : (
            <p className="text-sm text-content-muted">
              Your role can view extractions but not request them.
            </p>
          )}
          {request.isError ? (
            <Alert tone="danger" className="mt-3">
              {request.error instanceof Error ? request.error.message : 'That did not work.'}
            </Alert>
          ) : null}
        </CardBody>
      </Card>
    );
  }

  if (extraction.status === 'running') {
    return (
      <Card>
        <CardHeader title="Extraction" description={`Attempt ${extraction.attempt} is running.`} />
        <CardBody className="flex items-center gap-3">
          <Spinner className="size-5 text-brand-600" label={null} />
          <p className="text-sm text-content-muted">
            Reading the document. This usually takes under a minute.
          </p>
        </CardBody>
      </Card>
    );
  }

  if (extraction.status === 'failed') {
    return (
      <Card>
        <CardHeader title="Extraction failed" />
        <CardBody className="flex flex-col gap-3">
          <Alert tone="danger">
            {extraction.failureReason ?? 'The document could not be read.'}
          </Alert>
          {canRequest ? (
            <div>
              <Button variant="secondary" onClick={() => request.mutate(true)}>
                Try again
              </Button>
            </div>
          ) : null}
          <Provenance extraction={extraction} />
        </CardBody>
      </Card>
    );
  }

  const byPath = new Map(extraction.fields.map((field) => [field.fieldPath, field]));
  const issuesByField = groupIssues(extraction.issues);
  const lineItems = collectLineItems(extraction.fields);
  const documentIssues = extraction.issues.filter((i) => i.fieldPath === null);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title="Extraction"
          description="Read from the document by AI. Nothing here has been filed or posted."
        />
        <CardBody className="flex flex-col gap-4">
          {/* Findings first: they change how the numbers below should be read. */}
          <IssueList
            issues={[...documentIssues, ...extraction.issues.filter((i) => i.fieldPath !== null)]}
          />

          <Field
            label="Document type"
            field={
              byPath.get('documentType') ?? {
                fieldPath: 'documentType',
                value: extraction.documentType,
                confidence: extraction.documentTypeConfidence ?? 0,
                alternatives: [],
              }
            }
            issues={issuesByField.get('documentType') ?? []}
          />

          {FIELD_GROUPS.map((group) => {
            const fields = extraction.fields.filter(
              (field) => field.fieldPath.startsWith(group.prefix) && field.value !== null,
            );
            if (fields.length === 0) return null;

            return (
              <section key={group.prefix}>
                <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-content-subtle">
                  {group.title}
                </h3>
                <dl className="flex flex-col gap-1">
                  {fields.map((field) => (
                    <Field
                      key={field.fieldPath}
                      label={labelForFieldPath(field.fieldPath)}
                      field={field}
                      issues={issuesByField.get(field.fieldPath) ?? []}
                    />
                  ))}
                </dl>
              </section>
            );
          })}

          {/* Fields the model looked for and could not find. Shown separately
              and collapsed by default: a reviewer needs to know they were
              checked, but a list of blanks would drown the values that matter. */}
          <UnreadFields
            fields={extraction.fields.filter(
              (field) => field.value === null && !field.fieldPath.startsWith('lineItems'),
            )}
          />
        </CardBody>
      </Card>

      {lineItems.length > 0 ? <LineItems lines={lineItems} /> : null}

      <Card>
        <CardHeader title="How this was read" />
        <CardBody>
          <Provenance extraction={extraction} />
          {canRequest ? (
            <Button
              variant="ghost"
              size="sm"
              className="mt-3"
              onClick={() => request.mutate(true)}
              disabled={request.isPending}
            >
              Re-extract
            </Button>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Field({
  label,
  field,
  issues,
}: {
  label: string;
  field: ExtractedField;
  issues: readonly ExtractionIssue[];
}) {
  const band = confidenceBand(field.confidence);
  const style = BAND_STYLES[band];
  const hasError = issues.some((issue) => issue.severity === 'error');

  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border py-1 last:border-0">
      <dt className="shrink-0 text-sm text-content-muted">{label}</dt>
      <dd className="flex min-w-0 items-baseline gap-2 text-right">
        <span className={cn('truncate text-sm', hasError ? 'text-danger' : 'text-content')}>
          {formatValue(field.value)}
        </span>
        <span
          className="flex shrink-0 items-center gap-1"
          title={`${style.label}, ${Math.round(field.confidence * 100)}%`}
        >
          <span className={cn('size-1.5 rounded-full', style.dot)} aria-hidden="true" />
          <span className={cn('tabular text-xs', style.text)}>
            {Math.round(field.confidence * 100)}%
          </span>
        </span>
      </dd>
    </div>
  );
}

function IssueList({ issues }: { issues: readonly ExtractionIssue[] }) {
  if (issues.length === 0) {
    return (
      <Alert tone="success">
        Every check passed. The amounts, dates and references are consistent.
      </Alert>
    );
  }

  // Errors first: a reviewer works down the list, and the order should match
  // the order in which the findings matter.
  const ordered = [...issues].sort((a, b) => severityRank(a.severity) - severityRank(b.severity));

  return (
    <ul className="flex flex-col gap-2">
      {ordered.map((issue) => (
        <li key={issue.id}>
          <Alert tone={issue.severity === 'error' ? 'danger' : 'warning'}>
            {issue.fieldPath === null ? null : (
              <strong className="mr-1">{labelForFieldPath(issue.fieldPath)}:</strong>
            )}
            {issue.message}
          </Alert>
        </li>
      ))}
    </ul>
  );
}

function UnreadFields({ fields }: { fields: readonly ExtractedField[] }) {
  const [isOpen, setIsOpen] = useState(false);
  if (fields.length === 0) return null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="text-xs text-content-muted hover:underline"
      >
        {isOpen ? 'Hide' : 'Show'} {fields.length} field
        {fields.length === 1 ? '' : 's'} not found on this document
      </button>
      {isOpen ? (
        <p className="mt-2 text-xs text-content-subtle">
          {fields.map((field) => labelForFieldPath(field.fieldPath)).join(' · ')}
        </p>
      ) : null}
    </div>
  );
}

interface LineItemRow {
  readonly index: number;
  readonly fields: Map<string, ExtractedField>;
}

function LineItems({ lines }: { lines: readonly LineItemRow[] }) {
  return (
    <Card>
      <CardHeader title={`Line items (${lines.length})`} />
      <CardBody>
        {/* Its own scroll container: a wide table must never make the page
            scroll sideways. */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-content-subtle">
                <th className="py-1 pr-3 font-semibold">Description</th>
                <th className="py-1 pr-3 text-right font-semibold">Qty</th>
                <th className="py-1 pr-3 text-right font-semibold">Unit</th>
                <th className="py-1 text-right font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => {
                const serials = line.fields.get('serialNumbers')?.value;
                const brand = line.fields.get('equipmentBrand')?.value;
                return (
                  <tr key={line.index} className="border-b border-border last:border-0 align-top">
                    <td className="py-1.5 pr-3">
                      {formatValue(line.fields.get('description')?.value)}
                      {/* Serial numbers are the warranty trail, so they are
                          shown rather than hidden behind an expander. */}
                      {Array.isArray(serials) && serials.length > 0 ? (
                        <span className="block font-mono text-xs text-content-muted">
                          {serials.join(', ')}
                        </span>
                      ) : null}
                      {typeof brand === 'string' ? (
                        <span className="block text-xs text-content-subtle">{brand}</span>
                      ) : null}
                    </td>
                    <td className="tabular py-1.5 pr-3 text-right">
                      {formatValue(line.fields.get('quantity')?.value)}
                    </td>
                    <td className="tabular py-1.5 pr-3 text-right">
                      {formatValue(line.fields.get('unitCost')?.value)}
                    </td>
                    <td className="tabular py-1.5 text-right">
                      {formatValue(line.fields.get('extendedCost')?.value)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

function Provenance({ extraction }: { extraction: DocumentExtractionDetail }) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
      <dt className="text-content-muted">Model</dt>
      <dd className="text-content">{extraction.modelServed ?? extraction.modelName}</dd>
      <dt className="text-content-muted">Prompt</dt>
      <dd className="text-content">
        {extraction.promptName} v{extraction.promptVersion}
      </dd>
      <dt className="text-content-muted">Schema</dt>
      <dd className="text-content">v{extraction.schemaVersion}</dd>
      <dt className="text-content-muted">Attempt</dt>
      <dd className="text-content">{extraction.attempt}</dd>
      <dt className="text-content-muted">Reasoning effort</dt>
      {/* Where a temperature would be. Opus 4.7+ rejects the parameter, and
          showing a value we never sent would be a fiction. */}
      <dd className="text-content">{extraction.effort ?? 'default'}</dd>
      <dt className="text-content-muted">Confidence floor</dt>
      <dd className="text-content">{Math.round(extraction.confidenceFloor * 100)}%</dd>
      {extraction.meanConfidence === null ? null : (
        <>
          <dt className="text-content-muted">Average confidence</dt>
          <dd className="text-content">{Math.round(extraction.meanConfidence * 100)}%</dd>
        </>
      )}
      {extraction.durationMs === null ? null : (
        <>
          <dt className="text-content-muted">Took</dt>
          <dd className="text-content">{(extraction.durationMs / 1000).toFixed(1)} s</dd>
        </>
      )}
      {extraction.inputTokens === null ? null : (
        <>
          <dt className="text-content-muted">Tokens</dt>
          <dd className="text-content">
            {extraction.inputTokens.toLocaleString('en-CA')} in ·{' '}
            {(extraction.outputTokens ?? 0).toLocaleString('en-CA')} out
          </dd>
        </>
      )}
    </dl>
  );
}

/* -------------------------------------------------------------------------- */

function severityRank(severity: ExtractionIssue['severity']): number {
  return severity === 'error' ? 0 : severity === 'warning' ? 1 : 2;
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (Array.isArray(value)) return value.length === 0 ? '-' : value.join(', ');
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  // Anything else came back in a shape the schema does not describe, so it is
  // shown as JSON rather than as "[object Object]".
  return JSON.stringify(value);
}

function groupIssues(issues: readonly ExtractionIssue[]): Map<string, ExtractionIssue[]> {
  const grouped = new Map<string, ExtractionIssue[]>();
  for (const issue of issues) {
    if (issue.fieldPath === null) continue;
    const existing = grouped.get(issue.fieldPath) ?? [];
    existing.push(issue);
    grouped.set(issue.fieldPath, existing);
  }
  return grouped;
}

function collectLineItems(fields: readonly ExtractedField[]): LineItemRow[] {
  const rows = new Map<number, Map<string, ExtractedField>>();

  for (const field of fields) {
    const match = /^lineItems\[(\d+)\]\.(.+)$/.exec(field.fieldPath);
    if (match === null) continue;
    const index = Number(match[1]);
    const leaf = match[2] ?? '';
    const row = rows.get(index) ?? new Map<string, ExtractedField>();
    row.set(leaf, field);
    rows.set(index, row);
  }

  return [...rows.entries()]
    .sort(([a], [b]) => a - b)
    .map(([index, fieldMap]) => ({ index, fields: fieldMap }));
}
